import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeft, LoaderCircle, Plug, TriangleAlert } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router';
import { BlackbaudAddin } from '../components/blackbaud-addin';
import { BlackbaudBillingSetup, BlackbaudPostedInvoices } from '../components/blackbaud-billing';
import { CampusNetworkNote } from '../components/campus-network';
import { EmailConnection, EmailLog, EmailOptions } from '../components/email-intake';
import {
  ProvisioningAccounts,
  ProvisioningConnection,
  ProvisioningNaming,
  ProvisioningOrgUnits,
  ProvisioningPending,
  ProvisioningPolicies,
} from '../components/google-provisioning';
import { IntuneConnection, MosyleConnection } from '../components/mdm-connections';
import { OneRosterClasses, OneRosterConnection, OneRosterFields, OneRosterOptions, OneRosterRollup, OneRosterRoster, OneRosterTransfers } from '../components/oneroster';
import { ArubaConnection, ArubaSettings } from '../components/aruba-integration';
import { AsmConnection, DeviceSyncOptions, JamfProConnection, JamfSchoolConnection } from '../components/mdm-integrations';
import { IntegrationLogo } from '../components/integration-logo';
import { AddOnBadge, AddOnRequired } from '../components/plan';
import { CopyField, DataSourcePicker, IntegrationShell, useIntegrationMutations, type IntegrationFact } from '../components/integration-shell';
import { RequestIntegrationCard } from '../components/integration-request';
import { useToast } from '../components/toast';
import { Alert, Badge, Button, cn, EmptyState, Field, Input, PageHeader, PageLoader, ErrorState, Select, Spinner, Switch, Table, Td, Textarea, Th } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PROVIDER_FEATURE, usePlan } from '../lib/billing';
import { formatDate, formatRelative, PERSON_TYPE_LABEL } from '../lib/format';
import { useProvisioningAccess } from '../lib/google-provisioning';
import { useCategories } from '../lib/hooks';
import { isMailbox, providerPath, syncs } from '../lib/integrations';
import type { OneRosterIntegration } from '../lib/oneroster';
import type {
  ArubaIntegration,
  AsmIntegration,
  BlackbaudBillingIntegration,
  BlackbaudIntegration,
  EmailIntegration,
  GoogleIntegration,
  GoogleProvisioningIntegration,
  Integration,
  IntegrationProvider,
  IntuneIntegration,
  JamfProIntegration,
  JamfSchoolIntegration,
  MosyleIntegration,
  PersonType,
  RoleMapping,
} from '../lib/types';

// ---------------------------------------------------------------------------
// Blackbaud
// ---------------------------------------------------------------------------

function BlackbaudConnection({ integration }: { integration: BlackbaudIntegration }) {
  const { can } = useAuth();
  const toast = useToast();
  const { refresh } = useIntegrationMutations('BLACKBAUD');
  const bb = integration.blackbaud;
  const isAdmin = can('ADMIN');
  const [useSchoolApp, setUseSchoolApp] = useState(bb.appSource === 'school' || !bb.platformAppAvailable);
  const [clientId, setClientId] = useState(bb.schoolApp.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [subscriptionKey, setSubscriptionKey] = useState('');

  const saveApp = useMutation({
    mutationFn: () => api.put('/integrations/blackbaud/app-credentials', { useSchoolApp, clientId, clientSecret, subscriptionKey }),
    onSuccess: () => {
      refresh();
      setClientSecret('');
      setSubscriptionKey('');
      toast.success('Application settings saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const connect = useMutation({
    mutationFn: () => api.post<{ url: string }>('/integrations/blackbaud/authorize'),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const disconnect = useMutation({
    mutationFn: () => api.post('/integrations/blackbaud/disconnect'),
    onSuccess: () => {
      refresh();
      toast.success('Disconnected from Blackbaud');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Data source</h3>
        <DataSourcePicker integration={integration} />
      </section>

      {integration.mode === 'LIVE' && (
        <>
          <section className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">1. SKY API application</h3>
              <p className="text-sm text-slate-500">Blackbaud connects through a registered SKY API application.</p>
            </div>
            <div className="space-y-2">
              <label className={cn('flex items-start gap-3 rounded-lg border px-4 py-3', !bb.platformAppAvailable && 'opacity-60')}>
                <input
                  type="radio"
                  className="mt-1"
                  checked={!useSchoolApp}
                  disabled={!isAdmin || !bb.platformAppAvailable}
                  onChange={() => setUseSchoolApp(false)}
                />
                <span>
                  <span className="block text-sm font-medium text-slate-900">Edvora’s application (recommended)</span>
                  <span className="block text-xs text-slate-500">
                    {bb.platformAppAvailable
                      ? 'Approve the Edvora Campus Asset app in Blackbaud Marketplace, then connect below.'
                      : 'Not configured on this Edvora environment.'}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 rounded-lg border px-4 py-3">
                <input type="radio" className="mt-1" checked={useSchoolApp} disabled={!isAdmin} onChange={() => setUseSchoolApp(true)} />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-slate-900">Our school’s own SKY API application</span>
                  <span className="block text-xs text-slate-500">Use credentials from your Blackbaud developer account.</span>
                </span>
              </label>
            </div>
            {useSchoolApp && (
              <div className="grid gap-4 rounded-lg border border-slate-200 p-4 sm:grid-cols-2">
                <Field label="Application ID (OAuth client_id)">
                  <Input value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
                </Field>
                <Field label="Application secret">
                  <Input
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    disabled={!isAdmin}
                    placeholder={bb.schoolApp.hasClientSecret ? 'Saved - leave blank to keep' : ''}
                  />
                </Field>
                <Field label="Subscription key (Bb-Api-Subscription-Key)">
                  <Input
                    type="password"
                    value={subscriptionKey}
                    onChange={(e) => setSubscriptionKey(e.target.value)}
                    disabled={!isAdmin}
                    placeholder={bb.schoolApp.hasSubscriptionKey ? 'Saved - leave blank to keep' : ''}
                  />
                </Field>
                <div className="sm:col-span-2">
                  <CopyField label="Redirect URI" value={bb.redirectUri} hint="Add this exact URI to your application in the Blackbaud developer portal." />
                </div>
              </div>
            )}
            {isAdmin && (
              <Button loading={saveApp.isPending} onClick={() => saveApp.mutate()}>
                Save application settings
              </Button>
            )}
          </section>

          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">2. Authorize access</h3>
              <p className="text-sm text-slate-500">
                A Blackbaud user with the <strong>SKY API Data Sync</strong> role (or a manager role) signs in to grant read access.
              </p>
            </div>
            {bb.connected ? (
              <Alert tone="green" title={`Connected${bb.environmentName ? ` to ${bb.environmentName}` : ''}`}>
                {bb.connectedBy && <>Authorized by {bb.connectedBy}. </>}
                {bb.refreshTokenExpiresAt && <>Authorization renews with each sync (current expiry {formatDate(bb.refreshTokenExpiresAt)}).</>}
              </Alert>
            ) : (
              <Alert tone="amber">Not connected yet.</Alert>
            )}
            {isAdmin && (
              <div className="flex gap-2">
                <Button variant="primary" loading={connect.isPending} disabled={!bb.appSource} onClick={() => connect.mutate()}>
                  {bb.connected ? 'Reconnect Blackbaud' : 'Connect Blackbaud'}
                </Button>
                {bb.connected && (
                  <Button variant="ghost" loading={disconnect.isPending} onClick={() => disconnect.mutate()}>
                    Disconnect
                  </Button>
                )}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function BlackbaudSettings({ integration }: { integration: BlackbaudIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { update } = useIntegrationMutations('BLACKBAUD');
  const ready = integration.mode === 'DEMO' || integration.blackbaud.connected;
  const roles = useQuery({
    queryKey: ['blackbaud-roles', integration.mode, integration.blackbaud.connected],
    queryFn: () => api.get<{ roles: { id: string; name: string; baseRoleId: string | null }[]; mappings: RoleMapping[]; suggested: RoleMapping[] }>('/integrations/blackbaud/roles'),
    enabled: ready && isAdmin,
    retry: false,
  });

  const [mapping, setMapping] = useState<Record<string, PersonType | ''>>({});
  const [syncCandidates, setSyncCandidates] = useState(integration.settings.syncCandidates);
  const [candidateSchoolYear, setCandidateSchoolYear] = useState(integration.settings.candidateSchoolYear ?? '');
  const [deactivateMissing, setDeactivateMissing] = useState(integration.settings.deactivateMissing);

  useEffect(() => {
    if (!roles.data) return;
    const source = roles.data.mappings.length ? roles.data.mappings : roles.data.suggested;
    setMapping(Object.fromEntries(source.map((m) => [m.roleId, m.personType])));
  }, [roles.data]);

  const save = () => {
    const roleMappings = (roles.data?.roles ?? [])
      .filter((role) => mapping[role.id])
      .map((role) => ({ roleId: role.id, roleName: role.name, personType: mapping[role.id] }));
    update.mutate({ settings: { roleMappings, syncCandidates, candidateSchoolYear, deactivateMissing } });
  };

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-sm font-semibold text-slate-900">Role mapping</h3>
        <p className="mb-3 text-sm text-slate-500">Choose which Blackbaud roles sync, and what kind of person each becomes in Edvora.</p>
        {!ready ? (
          <Alert tone="amber">Connect Blackbaud to load your roles.</Alert>
        ) : !isAdmin ? (
          <Alert tone="blue">Only administrators can change role mappings.</Alert>
        ) : roles.isLoading ? (
          <Spinner />
        ) : roles.error ? (
          <Alert tone="red">{errorMessage(roles.error)}</Alert>
        ) : (
          <>
            {roles.data!.mappings.length === 0 && (
              <Alert tone="blue" className="mb-3">
                No mappings saved yet - suggested mappings are pre-filled based on role names.
              </Alert>
            )}
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <Table>
                <thead>
                  <tr>
                    <Th>Blackbaud role</Th>
                    <Th>Sync as</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {roles.data!.roles.map((role) => (
                    <tr key={role.id}>
                      <Td>
                        <p className="font-medium text-slate-900">{role.name}</p>
                        <p className="text-xs text-slate-500">
                          Role {role.id}
                          {role.baseRoleId && ` · base role ${role.baseRoleId}`}
                        </p>
                      </Td>
                      <Td className="w-56">
                        <Select value={mapping[role.id] ?? ''} onChange={(e) => setMapping({ ...mapping, [role.id]: e.target.value as PersonType | '' })}>
                          <option value="">Don’t sync</option>
                          {(Object.keys(PERSON_TYPE_LABEL) as PersonType[]).map((type) => (
                            <option key={type} value={type}>
                              {PERSON_TYPE_LABEL[type]}
                            </option>
                          ))}
                        </Select>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </section>

      <section className="max-w-xl space-y-4">
        <Switch
          label="Sync admissions candidates"
          description="Import candidates from Blackbaud admissions (useful for pre-assigning devices to incoming students)."
          checked={syncCandidates}
          disabled={!isAdmin}
          onChange={setSyncCandidates}
        />
        {syncCandidates && (
          <Field label="Candidate school year" hint="As labeled in Blackbaud, e.g. 2027 - 2028. Leave blank for all.">
            <Input value={candidateSchoolYear} onChange={(e) => setCandidateSchoolYear(e.target.value)} disabled={!isAdmin} />
          </Field>
        )}
        <Switch
          label="Mark missing people inactive"
          description="People no longer returned by Blackbaud become inactive. Their checkout history is kept."
          checked={deactivateMissing}
          disabled={!isAdmin}
          onChange={setDeactivateMissing}
        />
      </section>

      {isAdmin && (
        <Button variant="primary" loading={update.isPending} onClick={save}>
          Save sync settings
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Google
// ---------------------------------------------------------------------------

function GoogleConnection({ integration }: { integration: GoogleIntegration }) {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('ADMIN');
  const { update, refresh } = useIntegrationMutations('GOOGLE');
  const g = integration.google;
  const [source, setSource] = useState<'platform' | 'school'>(g.schoolServiceAccount || !g.platformServiceAccount ? 'school' : 'platform');
  const [keyJson, setKeyJson] = useState('');
  const [adminEmail, setAdminEmail] = useState(integration.settings.adminEmail ?? '');
  const [customerId, setCustomerId] = useState(integration.settings.customerId ?? 'my_customer');

  const saveKey = useMutation({
    mutationFn: () => api.put('/integrations/google/service-account', { serviceAccountJson: source === 'school' ? keyJson || null : null }),
    onSuccess: () => {
      refresh();
      setKeyJson('');
      toast.success('Service account saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const account = source === 'school' ? g.schoolServiceAccount : g.platformServiceAccount;

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Data source</h3>
        <DataSourcePicker integration={integration} />
      </section>

      {integration.mode === 'LIVE' && (
        <>
          <section className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">1. Service account</h3>
              <p className="text-sm text-slate-500">
                Edvora reads ChromeOS devices and audit logs with a Google Cloud service account using domain-wide delegation.
              </p>
            </div>
            <div className="space-y-2">
              <label className={cn('flex items-start gap-3 rounded-lg border px-4 py-3', !g.platformServiceAccount && 'opacity-60')}>
                <input type="radio" className="mt-1" checked={source === 'platform'} disabled={!isAdmin || !g.platformServiceAccount} onChange={() => setSource('platform')} />
                <span>
                  <span className="block text-sm font-medium text-slate-900">Edvora’s service account (recommended)</span>
                  <span className="block text-xs text-slate-500">
                    {g.platformServiceAccount ? 'Nothing to upload - just authorize its client ID below.' : 'Not configured on this Edvora environment.'}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 rounded-lg border px-4 py-3">
                <input type="radio" className="mt-1" checked={source === 'school'} disabled={!isAdmin} onChange={() => setSource('school')} />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-slate-900">Our own service account</span>
                  <span className="block text-xs text-slate-500">
                    {g.schoolServiceAccount ? `Using ${g.schoolServiceAccount.clientEmail}` : 'Upload a JSON key from your Google Cloud project.'}
                  </span>
                </span>
              </label>
            </div>
            {source === 'school' && isAdmin && (
              <div className="space-y-3 rounded-lg border border-slate-200 p-4">
                <input
                  type="file"
                  accept=".json,application/json"
                  className="text-sm"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) setKeyJson(await file.text());
                  }}
                />
                <Textarea
                  rows={4}
                  value={keyJson}
                  onChange={(e) => setKeyJson(e.target.value)}
                  placeholder={g.schoolServiceAccount ? 'A key is saved. Paste or upload a new one to replace it.' : '{ "type": "service_account", … }'}
                  className="font-mono text-xs"
                />
              </div>
            )}
            {isAdmin && (
              <Button loading={saveKey.isPending} disabled={source === 'school' && !keyJson && !g.schoolServiceAccount} onClick={() => saveKey.mutate()}>
                {source === 'school' ? 'Save service account key' : 'Use Edvora’s service account'}
              </Button>
            )}
          </section>

          <section className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">2. Authorize in Google Admin</h3>
              <p className="text-sm text-slate-500">
                In the Google Admin console go to <strong>Security → Access and data control → API controls → Manage domain-wide delegation</strong>, choose
                <strong> Add new</strong>, and enter:
              </p>
            </div>
            {account?.clientId ? (
              <CopyField label="Client ID" value={account.clientId} />
            ) : (
              <Alert tone="amber">Save a service account first to see its client ID.</Alert>
            )}
            <CopyField label="OAuth scopes" value={g.scopes.join(',')} />
          </section>

          <section className="max-w-xl space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">3. Admin to act as</h3>
              <p className="text-sm text-slate-500">A Google Workspace admin allowed to view Chrome devices and audit reports.</p>
            </div>
            <Field label="Admin email">
              <Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} disabled={!isAdmin} placeholder="it-admin@yourschool.org" />
            </Field>
            <Field label="Customer ID" hint='Leave as "my_customer" unless Google support tells you otherwise.'>
              <Input value={customerId} onChange={(e) => setCustomerId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
            </Field>
            {isAdmin && (
              <Button variant="primary" loading={update.isPending} onClick={() => update.mutate({ settings: { adminEmail, customerId } })}>
                Save
              </Button>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function GoogleSettings({ integration }: { integration: GoogleIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const categories = useCategories();
  const { update } = useIntegrationMutations('GOOGLE');
  const s = integration.settings;
  const [orgUnitPath, setOrgUnitPath] = useState(s.orgUnitPath ?? '');
  const [syncLoginEvents, setSyncLoginEvents] = useState(s.syncLoginEvents);
  const [lookback, setLookback] = useState(String(s.loginEventLookbackDays));
  const [autoLinkBySerial, setAutoLinkBySerial] = useState(s.autoLinkBySerial);
  const [autoCreateAssets, setAutoCreateAssets] = useState(s.autoCreateAssets);
  const [autoCreateCategoryId, setAutoCreateCategoryId] = useState(s.autoCreateCategoryId ?? '');

  return (
    <div className="max-w-xl space-y-6">
      <div className="space-y-2">
        <CampusNetworkNote>
          Device pages compare each Chromebook’s public IP address, and each sign-in’s, with your school’s to show whether it was on or off
          campus.
        </CampusNetworkNote>
        {integration.mode === 'DEMO' && <p className="text-xs text-slate-500">Demo devices use 203.0.113.24 and 203.0.113.40 while they’re on campus.</p>}
      </div>

      <Field label="Limit to organizational unit" hint="e.g. /Students. Includes child org units. Leave blank to sync every ChromeOS device.">
        <Input value={orgUnitPath} onChange={(e) => setOrgUnitPath(e.target.value)} disabled={!isAdmin} placeholder="/" />
      </Field>
      <Switch
        label="Import ChromeOS sign-in history"
        description="Sign-in, sign-out, and failed sign-in events from the Google Admin audit log."
        checked={syncLoginEvents}
        disabled={!isAdmin}
        onChange={setSyncLoginEvents}
      />
      {syncLoginEvents && (
        <Field label="First sync looks back (days)" hint="Later syncs import only new events. Google keeps up to 180 days.">
          <Input type="number" min={1} max={180} value={lookback} onChange={(e) => setLookback(e.target.value)} disabled={!isAdmin} className="w-32" />
        </Field>
      )}
      <Switch
        label="Link devices to assets by serial number"
        description="When an asset’s serial matches a Google device, link them automatically."
        checked={autoLinkBySerial}
        disabled={!isAdmin}
        onChange={setAutoLinkBySerial}
      />
      <Switch
        label="Create assets for new devices"
        description="Devices without a matching asset get one, tagged with the Admin console Asset ID when available."
        checked={autoCreateAssets}
        disabled={!isAdmin}
        onChange={setAutoCreateAssets}
      />
      {autoCreateAssets && (
        <Field label="Category for new assets">
          <Select value={autoCreateCategoryId} onChange={(e) => setAutoCreateCategoryId(e.target.value)} disabled={!isAdmin}>
            <option value="">Chromebook</option>
            {categories.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {isAdmin && (
        <Button
          variant="primary"
          loading={update.isPending}
          onClick={() =>
            update.mutate({
              settings: {
                orgUnitPath,
                syncLoginEvents,
                loginEventLookbackDays: Number(lookback) || 14,
                autoLinkBySerial,
                autoCreateAssets,
                autoCreateCategoryId,
              },
            })
          }
        >
          Save sync settings
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Integrations portal and each integration's page
// ---------------------------------------------------------------------------

function find<T extends Integration>(integrations: Integration[], provider: T['provider']) {
  return integrations.find((i): i is T => i.provider === provider);
}

type IntegrationTab = { value: string; label: string; content: ReactNode };

interface CatalogEntry {
  provider: IntegrationProvider;
  title: string;
  /** One line on its card. */
  summary: string;
  /** At the top of its page. */
  description?: string;
  tabs?(integration: Integration, all: Integration[]): IntegrationTab[];
  /** For integrations that don't sync: what the strip under the name shows. */
  facts?(integration: Integration): IntegrationFact[];
  /** Set up somewhere else instead of its own page. */
  href?: string;
}

/** Every integration a school can set up, in the order cards appear within active and inactive. */
const CATALOG: CatalogEntry[] = [
  {
    provider: 'BLACKBAUD',
    title: 'Blackbaud SKY API',
    summary: 'Students, faculty, and staff from Education Management, and devices on Blackbaud contact cards',
    description:
      'Sync students, teachers, staff, alumni, past students, and admissions candidates from Blackbaud Education Management, and show their devices on Blackbaud contact cards.',
    tabs: (integration) => {
      const blackbaud = integration as BlackbaudIntegration;
      return [
        { value: 'connection', label: 'Connection', content: <BlackbaudConnection integration={blackbaud} /> },
        { value: 'settings', label: 'Roles & options', content: <BlackbaudSettings integration={blackbaud} /> },
        { value: 'addin', label: 'SKY add-ins', content: <BlackbaudAddin integration={blackbaud} /> },
      ];
    },
  },
  {
    provider: 'BLACKBAUD_BILLING',
    title: 'Blackbaud Billing Management',
    summary: 'Damage invoices posted to families’ Blackbaud bills, over your Blackbaud SKY API connection',
    description:
      'Post damage invoices to Blackbaud Billing Management as a charge on the student, so the family sees it on their Blackbaud bill. Uses the connection from the Blackbaud SKY API integration.',
    tabs: (integration) => {
      const billing = integration as BlackbaudBillingIntegration;
      return [
        { value: 'setup', label: 'Setup', content: <BlackbaudBillingSetup integration={billing} /> },
        { value: 'posted', label: 'Posted invoices', content: <BlackbaudPostedInvoices /> },
      ];
    },
    facts: (integration) => {
      const { billing } = integration as BlackbaudBillingIntegration;
      return [
        {
          label: 'Blackbaud connection',
          value: billing.blackbaud.mode === 'DEMO' ? 'Demo mode' : billing.blackbaud.connected ? 'Connected' : 'Not connected',
          detail: billing.blackbaud.environmentName ?? undefined,
        },
        { label: 'Invoices posted', value: billing.postedInvoices.toLocaleString() },
        {
          label: 'Last posted',
          value: billing.lastPostedAt ? formatRelative(billing.lastPostedAt) : 'Never',
          detail: billing.lastPostedAt ? formatDate(billing.lastPostedAt) : undefined,
        },
      ];
    },
  },
  {
    provider: 'ONEROSTER',
    title: 'SIS rostering (OneRoster)',
    summary: 'Students, staff, schools, classes, and enrollments from PowerSchool, Genesis, Infinite Campus, Skyward, ClassLink, Veracross, or CSV files',
    description:
      'Roster from your student information system through OneRoster: students, staff, grade levels, schools, classes, and enrollments, synced on a schedule so people match the SIS, with transfers, SIS fields, and a year-end roll-up.',
    tabs: (integration) => {
      const oneRoster = integration as OneRosterIntegration;
      return [
        { value: 'connection', label: 'Connection', content: <OneRosterConnection integration={oneRoster} /> },
        { value: 'roster', label: 'Roster', content: <OneRosterRoster integration={oneRoster} /> },
        { value: 'options', label: 'Roles & options', content: <OneRosterOptions integration={oneRoster} /> },
        { value: 'fields', label: 'SIS fields', content: <OneRosterFields integration={oneRoster} /> },
        { value: 'classes', label: 'Classes', content: <OneRosterClasses /> },
        { value: 'transfers', label: 'Transfers', content: <OneRosterTransfers /> },
        { value: 'rollup', label: 'Year-end roll-up', content: <OneRosterRollup /> },
      ];
    },
  },
  {
    provider: 'GOOGLE',
    title: 'Google Workspace',
    summary: 'Chromebooks from Google Admin, with usage, recent users, and sign-in history',
    description: 'Sync ChromeOS devices from Google Admin with usage, recent users, auto-update dates, and sign-in history.',
    tabs: (integration) => {
      const google = integration as GoogleIntegration;
      return [
        { value: 'connection', label: 'Connection', content: <GoogleConnection integration={google} /> },
        { value: 'settings', label: 'Sync options', content: <GoogleSettings integration={google} /> },
      ];
    },
  },
  {
    provider: 'JAMF_PRO',
    title: 'Jamf Pro',
    summary: 'Macs, iPads, and Apple TVs from Jamf Pro inventory',
    description: 'Sync Macs, iPads, and Apple TVs from Jamf Pro inventory: OS version, last check-in, assigned user, site, and building.',
    tabs: (integration) => [
      { value: 'connection', label: 'Connection', content: <JamfProConnection integration={integration as JamfProIntegration} /> },
      { value: 'settings', label: 'Sync options', content: <DeviceSyncOptions integration={integration as JamfProIntegration} /> },
    ],
  },
  {
    provider: 'JAMF_SCHOOL',
    title: 'Jamf School',
    summary: 'iPads, Macs, and Apple TVs from Jamf School',
    description: 'Sync iPads, Macs, and Apple TVs from Jamf School: OS version, last check-in, owner, location, battery, and storage.',
    tabs: (integration) => [
      { value: 'connection', label: 'Connection', content: <JamfSchoolConnection integration={integration as JamfSchoolIntegration} /> },
      { value: 'settings', label: 'Sync options', content: <DeviceSyncOptions integration={integration as JamfSchoolIntegration} /> },
    ],
  },
  {
    provider: 'MOSYLE',
    title: 'Mosyle',
    summary: 'iPads, Macs, and Apple TVs from Mosyle Manager',
    description: 'Sync iPads, Macs, and Apple TVs from Mosyle Manager: OS version, last check-in, assigned user, battery, and storage.',
    tabs: (integration) => [
      { value: 'connection', label: 'Connection', content: <MosyleConnection integration={integration as MosyleIntegration} /> },
      { value: 'settings', label: 'Sync options', content: <DeviceSyncOptions integration={integration as MosyleIntegration} /> },
    ],
  },
  {
    provider: 'ARUBA_CENTRAL',
    title: 'HPE Aruba New Central',
    summary: 'Access points, switches and gateways from Aruba Central',
    description:
      'Sync the network equipment in your Aruba Central account: model, serial number, site, firmware and whether it’s online. Access points can keep Chromebook location’s BSSID list in step, and a smart rule can open a ticket when something has been offline too long.',
    tabs: (integration) => [
      { value: 'connection', label: 'Connection', content: <ArubaConnection integration={integration as ArubaIntegration} /> },
      { value: 'settings', label: 'Sync options', content: <ArubaSettings integration={integration as ArubaIntegration} /> },
    ],
  },
  {
    provider: 'INTUNE',
    title: 'Microsoft Intune',
    summary: 'Windows PCs, Macs, iPads, and Android devices from Intune',
    description:
      'Sync Windows PCs, Macs, iPads, and Android devices from Intune through Microsoft Graph: OS version, last check-in, user, compliance, encryption, and storage.',
    tabs: (integration) => [
      { value: 'connection', label: 'Connection', content: <IntuneConnection integration={integration as IntuneIntegration} /> },
      { value: 'settings', label: 'Sync options', content: <DeviceSyncOptions integration={integration as IntuneIntegration} /> },
    ],
  },
  {
    provider: 'APPLE_SCHOOL_MANAGER',
    title: 'Apple School Manager',
    summary: 'Apple purchases, MDM server assignments, and AppleCare coverage',
    description: 'Sync every device your school bought from Apple or a reseller: order details, MDM server assignment, and AppleCare coverage.',
    tabs: (integration) => [
      { value: 'connection', label: 'Connection', content: <AsmConnection integration={integration as AsmIntegration} /> },
      { value: 'settings', label: 'Sync options', content: <DeviceSyncOptions integration={integration as AsmIntegration} /> },
    ],
  },
  {
    provider: 'EMAIL',
    title: 'Help desk email',
    summary: 'Email to your help desk address becomes tickets, and replies go back by email',
    description:
      'Checks your help desk mailbox in Google Workspace or Microsoft 365. New emails become tickets, replies land on the ticket they belong to, and senders get a confirmation with their ticket number.',
    tabs: (integration, all) => {
      const email = integration as EmailIntegration;
      return [
        { value: 'connection', label: 'Connection', content: <EmailConnection integration={email} google={find<GoogleIntegration>(all, 'GOOGLE')} /> },
        { value: 'settings', label: 'Senders & tickets', content: <EmailOptions integration={email} /> },
        { value: 'log', label: 'Email log', content: <EmailLog /> },
      ];
    },
  },
  {
    provider: 'GOOGLE_PROVISIONING',
    title: 'Google Workspace Provisioning',
    summary: 'Google accounts for students, faculty, and staff in Blackbaud, named and organized your way, and suspended when they leave',
    description:
      'Create Google Workspace accounts for the people in Blackbaud with your naming conventions and org units, move them as classes change, and suspend them when people leave.',
    tabs: (integration, all) => {
      const provisioning = integration as GoogleProvisioningIntegration;
      return [
        {
          value: 'connection',
          label: 'Connection',
          content: (
            <ProvisioningConnection
              integration={provisioning}
              all={{ blackbaud: find<BlackbaudIntegration>(all, 'BLACKBAUD'), google: find<GoogleIntegration>(all, 'GOOGLE') }}
            />
          ),
        },
        { value: 'naming', label: 'Roles & naming', content: <ProvisioningNaming integration={provisioning} /> },
        { value: 'org-units', label: 'Org units', content: <ProvisioningOrgUnits integration={provisioning} /> },
        { value: 'policies', label: 'Departures & access', content: <ProvisioningPolicies integration={provisioning} /> },
        { value: 'pending', label: 'Pending users', content: <ProvisioningPending integration={provisioning} /> },
        { value: 'accounts', label: 'Accounts', content: <ProvisioningAccounts /> },
      ];
    },
  },
  {
    provider: 'FACILITIES_EMAIL',
    title: 'Facilities email',
    summary: 'Email to your facilities address becomes maintenance requests that your routing rules assign',
    href: '/facilities/setup?tab=email',
  },
];

function useIntegrations() {
  return useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.get<Integration[]>('/integrations'),
    refetchInterval: (q) => (q.state.data?.some(isRunning) ? 3000 : false),
  });
}

const isRunning = (integration: Integration) => integration.recentRuns.some((run) => run.status === 'QUEUED' || run.status === 'RUNNING');

/** Active integrations come first, then ones that are set up but turned off, then the rest. */
const rank = (integration: Integration) => (integration.enabled ? 2 : integration.status !== 'NOT_CONFIGURED' ? 1 : 0);

function CardStatus({ integration }: { integration: Integration }) {
  if (integration.enabled && integration.status === 'NOT_CONFIGURED') return <Badge tone="amber">Setup incomplete</Badge>;
  if (integration.enabled) return <Badge tone="green">Active</Badge>;
  if (integration.status !== 'NOT_CONFIGURED') return <Badge tone="gray">Off</Badge>;
  return <Badge tone="gray">Not set up</Badge>;
}

/** What a card says at the bottom for an integration that posts instead of syncing. */
function postedFooter(integration: BlackbaudBillingIntegration) {
  const { lastPostedAt } = integration.billing;
  if (lastPostedAt) return `Last posted an invoice ${formatRelative(lastPostedAt)}`;
  return integration.enabled && integration.status !== 'NOT_CONFIGURED' ? 'No invoices posted yet' : 'Not set up yet';
}

function IntegrationCard({ entry, integration }: { entry: CatalogEntry; integration: Integration }) {
  const running = isRunning(integration);
  const mailbox = isMailbox(integration.provider);
  const problem = integration.status === 'ERROR' || !!integration.lastError;
  const feature = PROVIDER_FEATURE[integration.provider];
  const footer =
    integration.provider === 'BLACKBAUD_BILLING'
      ? postedFooter(integration)
      : running
        ? mailbox
          ? 'Checking the mailbox now…'
          : 'Syncing now…'
        : integration.lastSuccessAt
          ? `${mailbox ? 'Checked' : 'Synced'} ${formatRelative(integration.lastSuccessAt)}`
          : integration.enabled
            ? `Hasn’t ${mailbox ? 'checked the mailbox' : 'synced'} yet`
            : 'Not connected yet';

  return (
    <Link
      to={entry.href ?? `/integrations/${providerPath(integration.provider)}`}
      className="group relative flex min-h-48 flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:outline-none"
    >
      <div className="absolute top-4 right-4 flex items-center gap-1.5">
        {problem && (
          <span title={integration.lastError ?? 'The last sync or test failed'} className="text-amber-500">
            <TriangleAlert className="size-4" aria-label="Needs attention" />
          </span>
        )}
        {integration.enabled && integration.mode === 'DEMO' && <Badge tone="violet">Demo</Badge>}
        <CardStatus integration={integration} />
      </div>
      <div className="flex items-center gap-3 pr-24">
        <IntegrationLogo provider={integration.provider} />
        <h2 className="text-lg leading-snug font-semibold text-slate-900 group-hover:text-brand-800">{entry.title}</h2>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-slate-600">{entry.summary}</p>
      <p className={cn('mt-auto flex items-center gap-1.5 pt-5 text-xs', problem ? 'text-amber-700' : 'text-slate-500')}>
        {running && <LoaderCircle className="size-3.5 animate-spin" />}
        {problem && !running ? 'Needs attention' : footer}
        {feature && <AddOnBadge feature={feature} className="ml-auto" />}
      </p>
    </Link>
  );
}

/** Every integration as a card, active ones first. Choosing one opens its settings. */
export function IntegrationsPage() {
  const { canFacilities } = useAuth();
  const [params] = useSearchParams();
  const query = useIntegrations();

  // Older links named the integration in the query string, like Blackbaud's sign-in did.
  const legacyProvider = params.get('provider');
  if (legacyProvider) {
    const rest = new URLSearchParams(params);
    rest.delete('provider');
    return <Navigate to={`/integrations/${legacyProvider}${rest.size ? `?${rest}` : ''}`} replace />;
  }

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const integrations = query.data;
  const cards = CATALOG.flatMap((entry, order) => {
    const integration = integrations.find((i) => i.provider === entry.provider);
    // The facilities mailbox is set up by facilities administrators, in Facilities setup.
    if (!integration || (entry.provider === 'FACILITIES_EMAIL' && !canFacilities('ADMIN'))) return [];
    return [{ entry, integration, order }];
  }).sort((a, b) => rank(b.integration) - rank(a.integration) || a.order - b.order);

  return (
    <>
      <PageHeader
        title="Integrations"
        description="The systems connected to your school and how they’re doing. Choose one to set it up. Credentials are encrypted at rest and never shown after saving."
      />
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map(({ entry, integration }) => (
          <IntegrationCard key={entry.provider} entry={entry} integration={integration} />
        ))}
        <RequestIntegrationCard />
      </div>
      <p className="mt-8 flex items-center gap-2 text-xs text-slate-500">
        <Plug className="size-3.5" /> Syncs run in the background. You can leave a page while they run.
      </p>
    </>
  );
}

/** One integration's connection, settings, and history, at /integrations/{integration}. */
export function IntegrationDetailPage() {
  const { provider: slug } = useParams();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const query = useIntegrations();
  const entry = CATALOG.find((item) => providerPath(item.provider) === slug && item.tabs);
  const oauthError = slug === 'blackbaud' ? params.get('error') : null;
  // Technicians use provisioning only once an administrator chooses them.
  const provisioningAccess = useProvisioningAccess(entry?.provider === 'GOOGLE_PROVISIONING');
  const plan = usePlan();

  useEffect(() => {
    if (slug === 'blackbaud' && params.get('connected') === '1') {
      toast.success('Blackbaud connected');
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const back = (
    <Link to="/integrations" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
      <ChevronLeft className="size-4" />
      Integrations
    </Link>
  );
  if (!entry?.tabs) {
    return (
      <>
        {back}
        <EmptyState icon={<Plug />} title="Integration not found" description="Choose one from the Integrations page." />
      </>
    );
  }
  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const integration = query.data.find((i) => i.provider === entry.provider);
  if (!integration) {
    return (
      <>
        {back}
        <EmptyState icon={<Plug />} title={`${entry.title} isn’t available`} description="It isn’t offered on this Edvora environment." />
      </>
    );
  }

  // Add-on integrations open once the plan includes them.
  const feature = PROVIDER_FEATURE[integration.provider];
  if (feature && !plan.has(feature)) {
    return (
      <>
        {back}
        <AddOnRequired feature={feature} description={entry.description ?? entry.summary} />
      </>
    );
  }

  return (
    <>
      {back}
      {oauthError && (
        <Alert tone="red" title="Blackbaud connection failed" className="mb-6">
          {oauthError}{' '}
          <button className="font-medium underline" onClick={() => setParams({}, { replace: true })}>
            Dismiss
          </button>
        </Alert>
      )}
      <IntegrationShell
        integration={integration}
        icon={<IntegrationLogo provider={integration.provider} size="lg" />}
        title={entry.title}
        description={entry.description ?? entry.summary}
        tabs={entry.tabs(integration, query.data)}
        syncAllowed={entry.provider !== 'GOOGLE_PROVISIONING' || !!provisioningAccess.data?.canUse}
        facts={syncs(integration.provider) ? undefined : entry.facts?.(integration)}
      />
    </>
  );
}
